import React, { useMemo, useState } from 'react';
import { postAssetRequest } from '../../api/assetsApi';
import CustomDatePicker from '../common/CustomDatePicker';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAssetRequests } from '../../redux/assets/actions';
import { toast } from 'react-toastify';
import { REQUEST_TO_RESERVE_ASSET_TOAST_MESSAGES } from '../../service/toastService';
import Modal from '../common/Modal';

function ReserveAssetModal({ show, onClose, onReserve, asset }) {
  const dispatch = useDispatch();
  const { selectedUser } = useSelector((state) => state.users);
  const { takeRequests } = useSelector((state) => state.assets);
  const [reservationForm, setReservationForm] = useState({ motivation: '', startingFrom: '' });
  const [reservationErrors, setReservationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const disabledDateRanges = useMemo(() => {
    if (!asset?.history || !Array.isArray(asset.history)) {
      return [];
    }

    return asset.history
      .filter((entry) => entry.loanDate)
      .map((entry) => {
        const startDate = new Date(entry.loanDate);
        const endDate = entry.returnDate ? new Date(entry.returnDate) : null;
        return { startDate, endDate };
      });
  }, [asset]);

  const handleReservationChange = (field, value) => {
    setReservationForm((prev) => ({ ...prev, [field]: value }));

    setReservationErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      if (field === 'motivation' && value && value.length > 255) {
        next.motivation = 'Motivation cannot exceed 255 characters.';
      }

      return next;
    });
  };

  const handleClose = () => {
    setReservationForm({ motivation: '', startingFrom: '' });
    setReservationErrors({});
    setSubmitting(false);
    setShowConfirm(false);
    onClose();
  };

  const validateReservationForm = () => {
    const errors = {};
    if (!reservationForm.motivation?.trim()) errors.motivation = 'Motivation is required.';
    if (reservationForm.motivation && reservationForm.motivation.length > 255) {
      errors.motivation = 'Motivation cannot exceed 255 characters.';
    }
    if (!reservationForm.startingFrom) errors.startingFrom = 'Starting date is required.';
    setReservationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenConfirm = () => {
    if (validateReservationForm()) {
      setShowConfirm(true);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handleSubmitReservation = async () => {
    if (!validateReservationForm()) {
      setShowConfirm(false);
      return;
    }

    const data = {
      inInventory: true,
      loanDate: new Date(reservationForm.startingFrom).toISOString(),
      motivation: reservationForm.motivation.trim(),
      assetId: asset.assetId,
    };

    try {
      setSubmitting(true);
      await postAssetRequest(data);
      if (selectedUser?.myportalUserId) {
        dispatch(fetchAssetRequests(selectedUser.myportalUserId, 0, takeRequests || 20));
      }
      setReservationForm({ motivation: '', startingFrom: '' });
      setReservationErrors({});
      toast.success('Successfully sent reservation request!', REQUEST_TO_RESERVE_ASSET_TOAST_MESSAGES.success);
      if (typeof onReserve === 'function') {
        onReserve(data);
      }
      handleClose();
    } catch (e) {
      let errorMessage = 'Failed to submit reservation. Please try again.';
      if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.message) {
        errorMessage = e.message;
      }

      setReservationErrors((prev) => ({ ...prev, submit: errorMessage }));
      toast.error(errorMessage, REQUEST_TO_RESERVE_ASSET_TOAST_MESSAGES.error);
      setSubmitting(false);
    }
  };

  if (!show || !asset) return null;

  return (
    <>
      {show && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            paddingTop: '60px',
            paddingBottom: '60px',
          }}>
          <div className="modal-dialog modal-lg" style={{ width: '90%', maxWidth: '900px' }}>
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="modal-title mb-0">Request Asset: {asset.name}</h5>
                </div>
                <div className="d-flex align-items-center">
                  <span
                    className="badge"
                    style={{
                      border: `2px solid ${getStatusColor(asset.assetStatus)}`,
                      color: getStatusColor(asset.assetStatus),
                      backgroundColor: 'transparent',
                      padding: '8px 16px',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                    }}>
                    {getStatusDisplayName(asset.assetStatus)}
                  </span>
                </div>
              </div>

              <div className="modal-body" style={{ minHeight: '350px' }}>
                <div className="row">
                  <div className="col-md-8">
                    <h6 className="mb-3 fw-bold" style={{ fontWeight: 'bold' }}>
                      Reserve Asset
                    </h6>
                    <div className="mb-3">
                      <label htmlFor="reserveMotivation" className="form-label fw-bold" style={{ fontWeight: 'bold' }}>
                        Motivation <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="reserveMotivation"
                        className={`form-control ${reservationErrors.motivation ? 'is-invalid' : ''}`}
                        rows="4"
                        placeholder="Please tell us why you want this asset."
                        value={reservationForm.motivation}
                        onChange={(e) => handleReservationChange('motivation', e.target.value)}
                      />
                      {reservationErrors.motivation && <div className="invalid-feedback">{reservationErrors.motivation}</div>}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="reserveStartingFrom" className="form-label fw-bold" style={{ fontWeight: 'bold' }}>
                        Starting from <span className="text-danger">*</span>
                      </label>
                      <CustomDatePicker
                        value={reservationForm.startingFrom}
                        onChange={(value) => handleReservationChange('startingFrom', value)}
                        placeholder="Select starting date"
                        className={`form-control ${reservationErrors.startingFrom ? 'is-invalid' : ''}`}
                        disabledDateRanges={disabledDateRanges}
                      />
                      {reservationErrors.startingFrom && <div className="invalid-feedback">{reservationErrors.startingFrom}</div>}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <h6 className="mb-3 fw-bold" style={{ fontWeight: 'bold' }}>
                      Asset Information
                    </h6>
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Name
                      </div>
                      <div>{asset.name}</div>
                    </div>
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Category
                      </div>
                      <div>{asset.assetCategory?.name || 'N/A'}</div>
                    </div>
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Tenant
                      </div>
                      <div>{asset.tenant?.name || 'N/A'}</div>
                    </div>
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Status
                      </div>
                      <div>{getStatusDisplayName(asset.assetStatus)}</div>
                    </div>
                  </div>
                </div>
                {reservationErrors.submit && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {reservationErrors.submit}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClose} style={{ width: '150px' }} disabled={submitting}>
                  CANCEL
                </button>
                <button type="button" className="btn btn-primary" onClick={handleOpenConfirm} disabled={submitting} style={{ width: '150px' }}>
                  {submitting ? 'SUBMITTING...' : 'SUBMIT'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal
        id="confirm-reserve-asset"
        keepOpen={showConfirm}
        backdrop
        title="Confirm Reservation"
        confirmText="Yes"
        cancelText="No"
        confirmHandler={handleSubmitReservation}
        closeHandler={handleCancelConfirm}>
        <div>
          <p className="modal-message text-center">Are you sure you want to reserve this asset? The reservation cannot be modified after submission.</p>
        </div>
      </Modal>
    </>
  );
}

const getStatusColor = (statusNumber) => {
  switch (statusNumber) {
    case 0:
      return '#28a745';
    case 1:
      return '#17a2b8';
    case 2:
      return '#ffc107';
    case 3:
      return '#dc3545';
    case 4:
      return '#007bff';
    default:
      return '#6c757d';
  }
};

const getStatusDisplayName = (statusNumber) => {
  switch (statusNumber) {
    case 0:
      return 'Available';
    case 1:
      return 'Spare';
    case 2:
      return 'In Repair';
    case 3:
      return 'Unavailable';
    case 4:
      return 'In Service';
    default:
      return 'Unknown';
  }
};

export default ReserveAssetModal;
